import { ComputedSerie, Datum, Layer } from "@nivo/line";
import {
    CurveFactory,
    curveLinear,
    curveMonotoneX,
    curveMonotoneY,
    curveNatural,
    curveStep,
    curveStepAfter,
    curveStepBefore,
    line,
} from "d3-shape";
import { ReactElement } from "react";
import { CustomAreaLayerGradientLayer, CustomLinearGradient } from "src/components/monitoring/senec-line-chart/ChartGradients";
import { LineChartItem } from "src/components/monitoring/senec-line-chart/lineChartTypes";

export const createCustomLineLayers = (
    lineChartItems: LineChartItem[],
    curve: "linear" | "monotoneX" | "monotoneY" | "natural" | "step" | "stepBefore" | "stepAfter",
): Layer[] =>
    lineChartItems
        .map((lineChartItem) => {
            const layers: Layer[] = [];

            if (!lineChartItem.hideLine) {
                layers.push(CustomLineLayer(lineChartItem.id, lineChartItem.linear, curve));
            }

            layers.push(CustomLinearGradient(lineChartItem.id, lineChartItem.color, lineChartItem.gradient === "SOFT"));
            layers.push(CustomAreaLayerGradientLayer(lineChartItem.id, curve));
            return layers;
        })
        .flat();

export const CustomLineLayer = (
    chartTypeId: string,
    linear?: boolean,
    curve?: "linear" | "monotoneX" | "monotoneY" | "natural" | "step" | "stepBefore" | "stepAfter",
): Layer => {
    return ({ series, xScale, yScale }) => {
        const specificLine = series.findIndex((line: ComputedSerie) => line.id === chartTypeId);
        return ChartLine({ lineIndex: specificLine, series, xScale, yScale, linear, curve });
    };
};
const ChartLine = ({
    lineIndex,
    series,
    xScale,
    yScale,
    linear = false,
    curve = "monotoneX",
}: {
    lineIndex: number;
    series: readonly ComputedSerie[];
    xScale(x: number): never;
    yScale(y: number): never;
    linear?: boolean;
    curve?: "linear" | "monotoneX" | "monotoneY" | "natural" | "step" | "stepBefore" | "stepAfter";
}): ReactElement => {
    if (lineIndex === -1) return <></>;

    const lineGenerator = line<Datum>()
        .x((d) => xScale(d.data.x))
        .y((d) => yScale(d.data.y))
        .defined((d) => d.data.y >= 0);

    if (!linear) lineGenerator.curve(getCurveType(curve));

    const linePath = lineGenerator(series[lineIndex].data) ?? undefined;

    return (
        <path
            data-testid={`custom-line-${series[lineIndex].id}`}
            d={linePath}
            fill="none"
            stroke={series[lineIndex].color}
            strokeWidth={"2"}
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    );
};

export const getCurveType = (
    curve: "linear" | "monotoneX" | "monotoneY" | "natural" | "step" | "stepBefore" | "stepAfter",
): CurveFactory => {
    switch (curve) {
        case "linear":
            return curveLinear;
        case "monotoneX":
            return curveMonotoneX;
        case "monotoneY":
            return curveMonotoneY;
        case "natural":
            return curveNatural;
        case "step":
            return curveStep;
        case "stepBefore":
            return curveStepBefore;
        case "stepAfter":
            return curveStepAfter;
    }
};
